import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  // TS - Custom override (TS-516): depth counter for the shared <body> scroll
  // lock. Dialog-components can nest (Quick View → cart drawer); the lock is
  // applied only by the first open and released only by the last close, so an
  // inner dialog closing can't unlock the page — or drop the scrollbar
  // compensation — from under a still-open outer dialog.
  static #bodyLockDepth = 0;

  // TS - Custom override (TS-516): scrollY captured when the lock engages.
  // The restore belongs to the lock, not to a dialog instance — a dialog
  // opened while the body was already fixed measures scrollY as 0, and
  // nested dialogs can close in either order.
  static #bodyLockScrollY = 0;

  // TS - Custom override (TS-516): whether THIS instance holds one unit of
  // the shared body lock. Ownership makes the release idempotent and lets
  // every exit path — closeDialog(), a native close (Escape-cancel when
  // focus escaped the component, form method="dialog"), or a disconnect
  // while open — release exactly the units it acquired, so a bypassed
  // closeDialog() can never wedge the depth counter and freeze the page,
  // and a dialog opened via a path that never incremented (direct
  // showModal()) can never unlock the page beneath another open dialog.
  #holdsBodyLock = false;

  connectedCallback() {
    super.connectedCallback();

    // TS - Custom override (TS-516): the native close event fires for every
    // close path, including ones that bypass closeDialog(); release the
    // body lock from there so no close path can leave the page frozen.
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // TS - Custom override (TS-516): a section re-render or theme-editor
    // unload can remove a dialog-component whose dialog is open — release
    // its unit of the body lock or the page stays locked forever.
    this.refs.dialog?.removeEventListener('close', this.#handleNativeClose);
    this.#releaseBodyLock();

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  // TS - Custom override (TS-516): native close paths (Escape-cancel while
  // focus sits outside the component, form method="dialog") skip
  // closeDialog() entirely — drop any stale closing class so the dialog
  // doesn't reopen mid-close state, and release this instance's lock unit.
  // After a closeDialog() close this is a harmless no-op.
  #handleNativeClose = () => {
    this.refs.dialog?.classList.remove('dialog-closing');
    this.#releaseBodyLock();
  };

  // TS - Custom override (TS-516): release this instance's unit of the
  // shared body lock; idempotent, so the multiple exit paths that can fire
  // for one close (closeDialog + the native close event) are harmless.
  // Whichever release drops the depth to 0 unlocks the body and restores
  // the scrollY captured when the lock engaged.
  #releaseBodyLock() {
    if (!this.#holdsBodyLock) return;
    this.#holdsBodyLock = false;
    DialogComponent.#bodyLockDepth -= 1;
    if (DialogComponent.#bodyLockDepth === 0) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.paddingInlineEnd = '';
      window.scrollTo({ top: DialogComponent.#bodyLockScrollY, behavior: 'instant' });
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // TS - Custom override (TS-516): the fixed-position lock below collapses
    // the document's overflow, dropping the root scrollbar — on classic
    // (space-consuming) scrollbars the viewport widens by the scrollbar's
    // width and every centered container re-centers. Measure the gap while
    // the scrollbar still exists; overlay scrollbars measure 0. When an
    // html-level overflow lock is already engaged, the scrollbar is already
    // gone and the width is held another way — by the scrollbar-gutter
    // ts-shared.css reserves (nav drawer, Searchspring filters) or by the
    // html padding the search overlay applies — so the innerWidth gap must
    // not be trusted (it would re-measure that reservation as a scrollbar).
    // The fixed body below sizes to the full viewport, which a reserved
    // gutter shrinks but html padding does not, so carry exactly the html
    // padding over as the body's compensation: 0 under the gutter locks,
    // the reserved strip under the search overlay's lock.
    const rootLocked = getComputedStyle(document.documentElement).overflowY === 'hidden';
    const scrollbarGap = rootLocked
      ? parseFloat(getComputedStyle(document.documentElement).paddingInlineEnd) || 0
      : window.innerWidth - document.documentElement.clientWidth;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // TS - Custom override (TS-516): a second showDialog before this frame
      // ran must not double-apply the lock or double-count the depth.
      if (dialog.open) return;

      // TS - Custom override (TS-516): only the first open applies the lock —
      // a nested open re-applying it would capture scrollY as 0 (the body is
      // already fixed) and snap the page background to the top. Reserve the
      // vanished scrollbar's width as body padding so the page keeps its
      // pre-lock layout width. Padding — not pinning the scrollbar track
      // (overflow-y: scroll) — because a track is browser chrome nothing can
      // paint over: an edge drawer could never sit flush against the screen
      // beside it. The padding strip, by contrast, is covered by a flush
      // drawer and dimmed by any .dialog-modal::backdrop, and top-layer
      // surfaces span the full scrollbar-free viewport.
      if (DialogComponent.#bodyLockDepth === 0) {
        DialogComponent.#bodyLockScrollY = scrollY;
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        if (scrollbarGap > 0) document.body.style.paddingInlineEnd = `${scrollbarGap}px`;
      }
      DialogComponent.#bodyLockDepth += 1;
      this.#holdsBodyLock = true;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // TS - Custom override (TS-516): concurrent closeDialog calls (Escape +
    // outside click) both pass the entry guard and await the same animation —
    // only the run that finds the dialog still open closes it and dispatches
    // DialogCloseEvent. A dialog closed natively mid-animation also lands
    // here; its lock was already released by the close-event listener.
    if (!dialog.open) return;

    // TS - Custom override (TS-516): only the last release unlocks the shared
    // <body> and its scrollbar compensation — a nested dialog closing (cart
    // drawer over Quick View) must not unlock or shift the page beneath a
    // still-open outer dialog. Released here (before dialog.close(), so the
    // scroll restore is immediate) and again harmlessly by the close event.
    this.#releaseBodyLock();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
